import {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";


import { useUIStore } from "../store";
import { Product, MenuData } from "../interfaces/domain/menu";
import { useOrderActions } from "./useOrderActions";
import { formatPrice } from "../utils/formatersPrecio";
import { findCategoryByProductId, findProductById } from "../utils/findInMenu";

export const usePlateModal = (
  plate: Product | null,
  onClose: () => void,
  cartItem?: any,
  menuData?: MenuData
) => {
  const { addItem, updateItem } = useOrderActions();

  const [isClosing, setIsClosing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(cartItem?.quantity ?? 1);
  const [selection, setSelection] = useState<Record<string, number>>({});
  const [optionSelection, setOptionSelection] = useState<Record<string, Record<string, number>>>({});

  const setSection = useUIStore((s) => s.setSection);

  const plateCategory = useMemo(() => {
    if (!menuData || !plate) return null;
    return findCategoryByProductId(menuData.categories, plate.id);
  }, [menuData, plate?.id]);

  // Resetear estado cuando cambia el producto
  useEffect(() => {
    if (!plate?.id) return;

    setIsClosing(false);
    setIsLoading(false);

    if (cartItem) {
      setQuantity(cartItem.quantity);
      setSelection(cartItem.comboSelection ?
        Object.fromEntries(cartItem.comboSelection.map((i: any) => [i.id, i.quantity]))
        : {}
      );
      setOptionSelection(cartItem.optionSelection ?? {});
    } else {
      setQuantity(1);
      setSelection({});
      setOptionSelection({});
    }
  }, [plate?.id, cartItem?.cartItemId]);

  const isCombo = useMemo(
    () => (plate?.combo?.items?.length ?? 0) > 0,
    [plate?.combo]
  );

  const toggleCheckItem = useCallback(
    (groupId: string, itemId: string) => {
      setOptionSelection((prev) => {
        const current = prev[groupId] ?? {};
        const next = { ...current };
        if (next[itemId]) delete next[itemId];
        else next[itemId] = 1;
        return { ...prev, [groupId]: next };
      });
    },
    []
  );

  const toggleRadioItem = useCallback(
    (groupId: string, itemId: string) => {
      setOptionSelection((prev) => {
        const isSelected = !!(prev[groupId]?.[itemId]);
        return { ...prev, [groupId]: isSelected ? {} : { [itemId]: 1 } };
      });
    },
    [],
  );

  const setOptionItemQty = useCallback(
    (groupId: string, itemId: string, newQty: number, maxSelection: number) => {
      setOptionSelection((prev) => {
        const current = prev[groupId] ?? {};
        const totalWithout = Object.entries(current)
          .filter(([k]) => k !== itemId)
          .reduce((acc, [, v]) => acc + v, 0);
        if (newQty > 0 && totalWithout + newQty > maxSelection) return prev;
        const next = { ...current };
        if (newQty <= 0) delete next[itemId];
        else next[itemId] = newQty;
        return { ...prev, [groupId]: next };
      });
    },
    [],
  );

  const isOptionsIncomplete = useMemo(() => {
    if (!plate?.options?.length) return false;
    return plate.options.some((group) => {
      if (group.min_selection <= 0) return false;
      const total = Object.values(optionSelection[group.id] ?? {}).reduce((a, b) => a + b, 0);
      return total < group.min_selection;
    });
  }, [plate?.options, optionSelection]);

  const optionsPrice = useMemo(() => {
    if (!plate?.options?.length) return 0;
    return plate.options.reduce((acc, group) => {
      const groupQtys = optionSelection[group.id] ?? {};
      return acc + group.items.reduce((a, item) => {
        return a + (groupQtys[item.id] ?? 0) * (item.price ?? 0);
      }, 0);
    }, 0);
  }, [plate?.options, optionSelection]);

  const totalPrice = ((plate?.price ?? 0) + optionsPrice) * quantity;

  const totalFaltanOptions = useMemo(() => {
    if (!plate?.options?.length) return 0;
    return plate.options.reduce((acc, group) => {
      if (group.min_selection <= 0) return acc;
      const total = Object.values(optionSelection[group.id] ?? {}).reduce((a, b) => a + b, 0);
      return acc + Math.max(0, group.min_selection - total);
    }, 0);
  }, [plate?.options, optionSelection]);

  const buttonText = useMemo(() => {
    if (isLoading) return cartItem ? "Modificando..." : "Agregando...";
    const action = cartItem ? "Modificar" : "Agregar";
    return `${action} · ${formatPrice(totalPrice)}`;
  }, [isLoading, cartItem, totalPrice]);

  const handleStartClosing = useCallback(() => {
    setIsClosing(true);
    window.history.replaceState({}, "", window.location.pathname);
    setSection(cartItem ? "cart" : "home");
    setTimeout(onClose, 320);
  }, [onClose, setSection, cartItem]);

  const comboSelectionFormatted = useMemo(() => {
    if (!isCombo || !plate) return undefined;

    // Aplanar choices de todos los items "select" del combo
    const allChoices = (plate.combo?.items ?? [])
      .filter((i: any) => i.type === "select")
      .flatMap((i: any) => i.choices ?? []);

    return Object.entries(selection).map(([id, qty]) => {
      const found = allChoices.find((c: any) => c.id === id)
        ?? (menuData ? findProductById(menuData.categories, id) : null);
      return { id, quantity: Number(qty), title: found?.title ?? "Producto" };
    });
  }, [isCombo, plate, selection, menuData]);

  const handleSubmit = () => {
    if (isLoading || !plate) return;

    if (isCombo) {
      const selectItem = plate?.combo?.items?.find((i: any) => i.type === "select");
      if (selectItem) {
        const totalSelected = Object.values(selection).reduce(
          (acc, value) => acc + Number(value), 0
        );
        if (totalSelected !== selectItem.quantity) return;
      }
    }

    setIsLoading(true);

    setTimeout(() => {
      const orderData: any = {
        ...plate,
        productId: plate.id,
        quantity,
        price: (plate.price ?? 0) + optionsPrice,
        comboSelection: isCombo ? comboSelectionFormatted : undefined,
        optionSelection: Object.keys(optionSelection).length > 0 ? optionSelection : undefined,
        resolvedOptions: (() => {
          const groups = [...(plate.options ?? [])]
            .sort((a, b) => (a.min_selection === 0 ? 0 : 1) - (b.min_selection === 0 ? 0 : 1))
            .map((group) => {
              const selected = Object.entries(optionSelection[group.id] ?? {})
                .filter(([, qty]) => qty > 0)
                .map(([itemId, qty]) => {
                  const item = group.items.find((i) => i.id === itemId);
                  return item ? { name: item.name, qty, price: item.price } : null;
                })
                .filter((x): x is { name: string; qty: number; price: number } => x !== null);
              return selected.length > 0 ? { groupName: group.name, items: selected } : null;
            })
            .filter((g): g is { groupName: string; items: { name: string; qty: number; price: number }[] } => g !== null);
          return groups.length > 0 ? groups : undefined;
        })(),
      };

      if (cartItem?.cartItemId) {
        updateItem(cartItem.cartItemId, orderData);
      } else {
        addItem(orderData);
      }

      handleStartClosing();
    }, 300);
  };

  return {
    quantity,
    setQuantity,
    selection,
    setSelection,
    optionSelection,
    setOptionSelection,
    toggleCheckItem,
    toggleRadioItem,
    setOptionItemQty,
    isOptionsIncomplete,
    totalFaltanOptions,
    isClosing,
    setIsClosing,
    setIsLoading,
    handleStartClosing,
    handleSubmit,
    isCombo,
    totalPrice,
    isLoading,
    buttonText,
    plateCategory,
  };
};
